/**
 * Parsers for settlement / bank reconciliation files.
 * Mercado Pago settlement CSV is the primary mapping reference.
 */

const MERCADO_PAGO_REQUIRED_HEADERS = [
    'SOURCE_ID',
    'PAYMENT_METHOD_TYPE',
    'TRANSACTION_TYPE',
    'TRANSACTION_AMOUNT',
    'FEE_AMOUNT',
    'REAL_AMOUNT',
];

/**
 * Maps settlement payment types to order/transaction codes.
 * Empty / unknown → null (não importar).
 * Pré-pago → Crédito; PIX/Transferência → PIX.
 */
export function mapSettlementPaymentMethod(raw) {
    const key = String(raw || '').trim().toLowerCase();
    if (!key) {
        return null;
    }
    if (key === 'debit_card' || key === 'debit' || key === 'débito' || key === 'debito') {
        return { matchKey: 'debit', apiCode: 'debit_card', label: 'Débito' };
    }
    if (
        key === 'credit_card'
        || key === 'credit'
        || key === 'prepaid_card'
        || key === 'crédito'
        || key === 'credito'
        || key === 'pré-pago'
        || key === 'pre-pago'
        || key === 'prepago'
    ) {
        return { matchKey: 'credit', apiCode: 'credit_card', label: 'Crédito' };
    }
    if (key === 'bank_transfer' || key === 'pix' || key.includes('pix')) {
        return { matchKey: 'pix', apiCode: 'pix', label: 'PIX' };
    }
    return null;
}

export function orderPaymentMatchKey(name) {
    const mapped = mapSettlementPaymentMethod(name);
    if (mapped) {
        return mapped.matchKey;
    }
    const key = String(name || '')
        .trim()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .toLowerCase();
    if (key.includes('debito')) {
        return 'debit';
    }
    if (key.includes('credito') || key.includes('pre-pago') || key.includes('prepago')) {
        return 'credit';
    }
    if (key === 'pix' || key.includes('pix')) {
        return 'pix';
    }
    return null;
}

function parseNumber(value) {
    if (value == null || value === '') {
        return 0;
    }
    if (typeof value === 'number') {
        return Number.isFinite(value) ? value : 0;
    }
    const normalized = String(value).trim().replace(/\s/g, '').replace(',', '.');
    const n = Number(normalized);
    return Number.isFinite(n) ? n : 0;
}

function parseDateTime(value) {
    if (!value) {
        return null;
    }
    const raw = String(value).trim();
    if (!raw) {
        return null;
    }
    const d = new Date(raw);
    if (Number.isNaN(d.getTime())) {
        return null;
    }
    return d.toISOString();
}

/** YYYY-MM-DD no fuso America/Sao_Paulo (mesmo critério das vendas). */
export function dateKeySaoPaulo(isoOrRaw) {
    if (!isoOrRaw) {
        return '';
    }
    const d = new Date(isoOrRaw);
    if (Number.isNaN(d.getTime())) {
        const s = String(isoOrRaw).trim();
        return s.length >= 10 ? s.slice(0, 10) : '';
    }
    return d.toLocaleDateString('en-CA', { timeZone: 'America/Sao_Paulo' });
}

function formatDateDisplay(isoOrRaw) {
    if (!isoOrRaw) {
        return '—';
    }
    const d = new Date(isoOrRaw);
    if (Number.isNaN(d.getTime())) {
        const s = String(isoOrRaw);
        return s.length >= 10 ? s.slice(0, 10) : s;
    }
    return d.toLocaleString('pt-BR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
}

export function paymentMethodLabel(method) {
    const mapped = mapSettlementPaymentMethod(method);
    if (mapped) {
        return mapped.label;
    }
    const key = String(method || '').trim();
    return key || '—';
}

function directionFromAmount(amount) {
    return amount < 0 ? 'outcome' : 'income';
}

function normalizeEntry(partial) {
    const chargedAmount = parseNumber(partial.chargedAmount);
    const releasedAmount = parseNumber(partial.releasedAmount);
    const feeAmount = parseNumber(partial.feeAmount);
    const discountAmount = Math.abs(feeAmount);
    const direction = directionFromAmount(chargedAmount !== 0 ? chargedAmount : releasedAmount);
    const mapped = mapSettlementPaymentMethod(partial.paymentMethod);
    const businessUnit = String(partial.businessUnit || '').trim();
    const subUnit = String(partial.subUnit || '').trim();
    const source = String(partial.source || '').trim()
        || [businessUnit, subUnit].filter(Boolean).join(' ');

    return {
        id: String(partial.id || ''),
        direction,
        directionLabel: direction === 'income' ? 'Entrada' : 'Saída',
        paymentMethod: String(partial.paymentMethod || '').trim(),
        paymentMethodLabel: mapped ? mapped.label : '—',
        matchKey: mapped ? mapped.matchKey : null,
        apiPaymentMethod: mapped ? mapped.apiCode : null,
        importable: Boolean(mapped),
        chargedAmount,
        chargedCents: Math.round(Math.abs(chargedAmount) * 100),
        releasedAmount,
        releasedCents: Math.round(Math.abs(releasedAmount) * 100),
        feeAmount,
        discountAmount,
        discountCents: Math.round(discountAmount * 100),
        taxesAmount: parseNumber(partial.taxesAmount),
        transactionType: String(partial.transactionType || '').trim(),
        transactionDate: parseDateTime(partial.transactionDate),
        settlementDate: parseDateTime(partial.settlementDate),
        moneyReleaseDate: parseDateTime(partial.moneyReleaseDate),
        businessUnit,
        subUnit,
        description: String(partial.description || '').trim()
            || [businessUnit, subUnit].filter(Boolean).join(' · '),
        source,
        saleDateKey: dateKeySaoPaulo(parseDateTime(partial.transactionDate) || partial.transactionDate),
        raw: partial.raw || null,
    };
}

function detectDelimiter(headerLine) {
    const commas = (headerLine.match(/,/g) || []).length;
    const semis = (headerLine.match(/;/g) || []).length;
    return semis >= commas ? ';' : ',';
}

function splitCsvLine(line, delimiter) {
    const result = [];
    let current = '';
    let inQuotes = false;
    for (let i = 0; i < line.length; i += 1) {
        const ch = line[i];
        if (ch === '"') {
            if (inQuotes && line[i + 1] === '"') {
                current += '"';
                i += 1;
            } else {
                inQuotes = !inQuotes;
            }
            continue;
        }
        if (ch === delimiter && !inQuotes) {
            result.push(current);
            current = '';
            continue;
        }
        current += ch;
    }
    result.push(current);
    return result;
}

function parseCsvText(text) {
    const cleaned = String(text || '').replace(/^\uFEFF/, '');
    const lines = cleaned.split(/\r?\n/).filter((line) => line.trim() !== '');
    if (lines.length === 0) {
        return { headers: [], rows: [] };
    }
    const delimiter = detectDelimiter(lines[0]);
    const headers = splitCsvLine(lines[0], delimiter).map((h) => h.trim());
    const rows = lines.slice(1).map((line) => {
        const cols = splitCsvLine(line, delimiter);
        const row = {};
        headers.forEach((header, index) => {
            row[header] = cols[index] != null ? cols[index].trim() : '';
        });
        return row;
    });
    return { headers, rows };
}

function isMercadoPagoSettlementCsv(headers) {
    const set = new Set(headers.map((h) => h.toUpperCase()));
    return MERCADO_PAGO_REQUIRED_HEADERS.every((h) => set.has(h));
}

export function parseMercadoPagoSettlementCsv(text) {
    const { headers, rows } = parseCsvText(text);
    if (!isMercadoPagoSettlementCsv(headers)) {
        throw new Error('CSV não reconhecido como liquidação do Mercado Pago (colunas esperadas: SOURCE_ID, TRANSACTION_AMOUNT, REAL_AMOUNT, FEE_AMOUNT…).');
    }

    const entries = rows.map((row) => normalizeEntry({
        id: row.SOURCE_ID,
        paymentMethod: row.PAYMENT_METHOD_TYPE,
        transactionType: row.TRANSACTION_TYPE,
        chargedAmount: row.TRANSACTION_AMOUNT,
        feeAmount: row.FEE_AMOUNT,
        releasedAmount: row.REAL_AMOUNT,
        taxesAmount: row.TAXES_AMOUNT,
        transactionDate: row.TRANSACTION_DATE,
        settlementDate: row.SETTLEMENT_DATE,
        moneyReleaseDate: row.MONEY_RELEASE_DATE,
        businessUnit: row.BUSINESS_UNIT,
        subUnit: row.SUB_UNIT,
        raw: row,
    }));

    return {
        source: 'mercado_pago_settlement_csv',
        sourceLabel: 'Mercado Pago (liquidação CSV)',
        entries,
    };
}

function decodeOfxAmount(value) {
    let raw = String(value || '').trim();
    if (!raw) {
        return 0;
    }
    let negative = false;
    if (raw.endsWith('-')) {
        negative = true;
        raw = raw.slice(0, -1);
    }
    if (raw.startsWith('+')) {
        raw = raw.slice(1);
    }
    if (raw.startsWith('-')) {
        negative = true;
        raw = raw.slice(1);
    }
    const amount = parseNumber(raw);
    return negative ? -Math.abs(amount) : amount;
}

function extractOfxTag(block, tag) {
    const re = new RegExp(`<${tag}>([^<\\r\\n]*)`, 'i');
    const match = block.match(re);
    return match ? match[1].trim() : '';
}

export function parseOfxOrOfd(text) {
    const content = String(text || '');
    const blocks = content.match(/<STMTTRN>[\s\S]*?(?:<\/STMTTRN>|(?=<STMTTRN>))/gi) || [];
    if (blocks.length === 0) {
        throw new Error('Nenhuma transação encontrada no arquivo OFX/OFD.');
    }

    const entries = blocks.map((block, index) => {
        const amount = decodeOfxAmount(extractOfxTag(block, 'TRNAMT'));
        const fitid = extractOfxTag(block, 'FITID') || `ofx-${index + 1}`;
        const memo = extractOfxTag(block, 'MEMO') || extractOfxTag(block, 'NAME') || '';
        const trnType = extractOfxTag(block, 'TRNTYPE') || '';
        const dtPosted = extractOfxTag(block, 'DTPOSTED') || '';
        let transactionDate = null;
        const m = dtPosted.match(/^(\d{4})(\d{2})(\d{2})/);
        if (m) {
            transactionDate = `${m[1]}-${m[2]}-${m[3]}T12:00:00.000Z`;
        }

        return normalizeEntry({
            id: fitid,
            paymentMethod: '',
            transactionType: trnType,
            chargedAmount: amount,
            feeAmount: 0,
            releasedAmount: amount,
            taxesAmount: 0,
            transactionDate,
            settlementDate: transactionDate,
            moneyReleaseDate: transactionDate,
            businessUnit: '',
            subUnit: '',
            description: memo,
            source: '',
            raw: { FITID: fitid, TRNAMT: String(amount), MEMO: memo, TRNTYPE: trnType, DTPOSTED: dtPosted },
        });
    });

    return {
        source: 'ofx_ofd',
        sourceLabel: 'Extrato OFX/OFD',
        entries,
    };
}

function extensionOf(fileName) {
    const parts = String(fileName || '').toLowerCase().split('.');
    return parts.length > 1 ? parts[parts.length - 1] : '';
}

export function parseSettlementFile(fileName, text) {
    const ext = extensionOf(fileName);
    const trimmed = String(text || '').trim();
    if (!trimmed) {
        throw new Error('Arquivo vazio.');
    }

    const looksLikeOfx = /OFXHEADER|<OFX>|<STMTTRN>/i.test(trimmed)
        || ext === 'ofx'
        || ext === 'ofd';

    if (looksLikeOfx && (ext === 'ofx' || ext === 'ofd' || /<STMTTRN>/i.test(trimmed))) {
        return parseOfxOrOfd(trimmed);
    }

    if (ext === 'csv' || trimmed.includes('SOURCE_ID') || trimmed.includes('TRANSACTION_AMOUNT')) {
        return parseMercadoPagoSettlementCsv(trimmed);
    }

    if (looksLikeOfx) {
        return parseOfxOrOfd(trimmed);
    }

    throw new Error('Formato não suportado. Envie um CSV de liquidação do Mercado Pago, OFX ou OFD.');
}

export function summarizeEntries(entries) {
    const list = Array.isArray(entries) ? entries : [];
    let incomeCharged = 0;
    let incomeReleased = 0;
    let outcomeCharged = 0;
    let outcomeReleased = 0;
    let totalDiscount = 0;
    let skipped = 0;

    list.forEach((entry) => {
        if (!entry.importable) {
            skipped += 1;
            return;
        }
        totalDiscount += entry.discountAmount || 0;
        if (entry.direction === 'outcome') {
            outcomeCharged += Math.abs(entry.chargedAmount || 0);
            outcomeReleased += Math.abs(entry.releasedAmount || 0);
        } else {
            incomeCharged += entry.chargedAmount || 0;
            incomeReleased += entry.releasedAmount || 0;
        }
    });

    return {
        count: list.length,
        importableCount: list.filter((e) => e.importable).length,
        skippedCount: skipped,
        incomeCount: list.filter((e) => e.importable && e.direction === 'income').length,
        outcomeCount: list.filter((e) => e.importable && e.direction === 'outcome').length,
        incomeCharged,
        incomeReleased,
        outcomeCharged,
        outcomeReleased,
        totalDiscount,
        netReleased: incomeReleased - outcomeReleased,
    };
}

/** Date range YYYY-MM-DD from entry dates (America/Sao_Paulo). */
export function settlementDateRange(entries) {
    const dates = (entries || [])
        .filter((e) => e.importable && (e.saleDateKey || e.transactionDate))
        .map((e) => e.saleDateKey || dateKeySaoPaulo(e.transactionDate))
        .filter((d) => /^\d{4}-\d{2}-\d{2}$/.test(d))
        .sort();
    if (dates.length === 0) {
        return null;
    }
    return {
        from: dates[0],
        to: dates[dates.length - 1],
        fromLabel: `${dates[0]} 00:00:00`,
        toLabel: `${dates[dates.length - 1]} 23:59:59`,
    };
}

export function formatSettlementDate(isoOrRaw) {
    return formatDateDisplay(isoOrRaw);
}

export function reaisToCents(value) {
    return Math.round(Math.abs(parseNumber(value)) * 100);
}
